import React from "react";
import Icon from "components/AppIcon";
import { useConfig } from "../../../context/ConfigContext";

const ValueChannelForm = ({
  formData,
  errors,
  onInputChange,
  isValidating,
}) => {
  const { config } = useConfig();

  if (!config) return null; // Wait for config to load

  // Fix business logic: Revenue channel pays per unit, NOT per pound
  const valueChannelRevenue = (parseFloat(formData.units) || 0) * config.value_channel_rate;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
          Value Channel Revenue
        </h3>
        <p className="text-sm text-text-secondary">
          Calculate revenue potential from value channel (Units × ${config.value_channel_rate.toFixed(2)})
        </p>
      </div>

      <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
        {/* Units - Read-only from Step 1 */}
        <div className="space-y-2">
          <label
            htmlFor="units"
            className="block text-sm font-body-medium text-text-primary"
          >
            <div className="flex items-center space-x-2">
              <Icon name="Hash" size={16} className="text-primary" />
              <span>Units</span>
              <span className="text-xs text-text-tertiary">(from Step 1)</span>
            </div>
          </label>

          <div className="relative">
            <input
              id="units"
              type="text"
              value={formData.units || ""}
              readOnly
              disabled
              className="block w-full px-4 py-3 border rounded-lg text-sm bg-secondary-100 text-text-secondary border-border cursor-not-allowed"
              aria-describedby="units-help"
            />
          </div>

          {/* Help Text */}
          <p id="units-help" className="text-xs text-text-tertiary">
            Number of units from Step 1 used to calculate value channel revenue
            potential
          </p>
        </div>

        {/* Material Weight - Read-only from Step 1 */}
        <div className="space-y-2">
          <label
            htmlFor="materialWeight"
            className="block text-sm font-body-medium text-text-primary"
          >
            <div className="flex items-center space-x-2">
              <Icon name="Weight" size={16} className="text-primary" />
              <span>Material Weight (LBS)</span>
              <span className="text-xs text-text-tertiary">(from Step 1)</span>
            </div>
          </label>

          <div className="relative">
            <input
              id="materialWeight"
              type="text"
              value={formData.materialWeight || ""}
              readOnly
              disabled
              className="block w-full px-4 py-3 border rounded-lg text-sm bg-secondary-100 text-text-secondary border-border cursor-not-allowed"
              aria-describedby="materialWeight-help"
            />
          </div>

          {/* Help Text */}
          <p id="materialWeight-help" className="text-xs text-text-tertiary">
            Material weight from Step 1 for reference
          </p>
        </div>

        {/* Revenue Preview */}
        <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
          <div className="flex items-center space-x-3 mb-3">
            <Icon name="TrendingUp" size={20} className="text-primary" />
            <h4 className="text-sm font-body-medium text-text-primary">
              Revenue Preview
            </h4>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-text-secondary">Unit Rate:</span>
              <span className="font-data text-text-primary">${config.value_channel_rate.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-text-secondary">Units:</span>
              <span className="font-data text-text-primary">
                {formData.units || "0"}
              </span>
            </div>
            <div className="pt-2 border-t border-primary-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-body-medium text-text-primary">
                  Total Value Channel Revenue:
                </span>
                <span className="text-lg font-data text-text-primary">
                  $
                  {valueChannelRevenue.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Information Card */}
        <div className="p-4 bg-surface-50 rounded-lg border border-border-light">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} className="text-primary mt-0.5" />
            <div>
              <h5 className="text-sm font-body-medium text-text-primary mb-1">
                About Value Channel Revenue
              </h5>
              <p className="text-xs text-text-secondary">
                Value channel revenue is calculated by multiplying the number of
                units by ${config.value_channel_rate.toFixed(2)} per unit. This represents the potential income
                from selling processed textiles through premium value channels.
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ValueChannelForm;
